import classNames from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { circleApi } from 'src/api/circles';
import { CircleConstants } from 'src/api/constants/circle.constants';
import { usersApi } from 'src/api/user';
import { UploadIcon } from 'src/assets/icons/upload-icon';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styles from './Video.module.scss';

interface UploadVideoProps {
  defaultVideo: string;
  onUpdate: (fileId: string | File) => void;
  classNameVideoContainer?: string;
  children?: React.ReactNode;
  helperText?: string;
  commentType?: string;
  hideDesc?: boolean;
}

export const UploadVideo: FC<UploadVideoProps> = ({
  defaultVideo,
  onUpdate,
  classNameVideoContainer,
  commentType,
  hideDesc,
}) => {
  const [previewVideo, setPreviewVideo] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (defaultVideo) {
      setPreviewVideo(defaultVideo);
    }
  }, [defaultVideo]);

  const handleVideoClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    fileRef.current?.click();
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      await handleFileUpload(uploadedFile);
    }
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await usersApi.uploadFile({
        contentType: file.type,
        name: file.name,
        type: commentType ? commentType : 'video-welcome',
      });
      const { uploadURL, key } = response;

      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      const previewUrl = URL.createObjectURL(file);
      setPreviewVideo(previewUrl);

      const videoId = await circleApi.getVideoId(key);
      onUpdate(videoId);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Check the console for more details.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.text}>
        <div className={styles.title}>Video</div>
        {/* {!hideDesc && (
          <div className={styles.infoIcon}>
            <a
              href="/how-it-works#info-video"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.infoIcon}
            >
              <InfoOutlinedIcon style={{ color: 'red', cursor: 'pointer' }} />
            </a>
          </div>
        )} */}
      </div>
      {!hideDesc && (
        <div className={styles.description}>
          Similar to the Description, this is your chance to pitch the purpose
          and value of your Circle.
        </div>
      )}
      <div
        className={classNames(styles.videoContainer, classNameVideoContainer, {
          [styles.dragging]: isDragging,
        })}
        onClick={handleVideoClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!previewVideo && !isUploading && (
          <div className={styles.videoWrapper}>
            <div>
              <div className={styles.icon}>
                <UploadIcon />
              </div>
              <div className={styles.uploadText}>Select video to upload</div>
              <div className={styles.dragText}>Or drag and drop</div>
            </div>
          </div>
        )}

        {isUploading && (
          <MaterialCircularProgress
            size={30}
            height="200px"
            message="Uploading..."
          />
        )}
        {previewVideo && !isUploading && (
          <video
            src={previewVideo}
            controls
            className={styles.previewVideo}
            style={{ width: '100%', height: '350px', display: 'block' }}
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>
      <div className={styles.maxDurationText}>
        Maximum {CircleConstants.maxChallengeLengthInSeconds} seconds
      </div>
      <input
        accept=".mp4, .mov, .avi"
        type="file"
        className={styles.hiddenInput}
        ref={fileRef}
        onChange={handleInputChange}
      />
    </div>
  );
};
